import React, { useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { Inbox as InboxIcon, Megaphone, Search, User } from 'lucide-react';
import {
  useInboxItems,
  useMarkAnnouncementRead,
  useMarkPersonalNotificationRead,
} from '../hooks/api';
import { Button, EmptyState, Panel, StatusPill, ViewTabs } from './ui';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'personal', label: 'Personal' },
];

const PRIORITY_PILL = {
  low: { status: 'In Progress Soft', label: 'Low' },
  medium: { status: 'Completed', label: 'Medium' },
  high: { status: 'Delayed', label: 'High' },
  critical: { status: 'At Risk', label: 'Critical' },
};

const Inbox = () => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const pageSize = 20;

  const { data, isLoading } = useInboxItems({
    filter,
    search,
    page,
    page_size: pageSize,
  });
  const markAnnouncementRead = useMarkAnnouncementRead();
  const markPersonalRead = useMarkPersonalNotificationRead();

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleOpen = (item) => {
    const key = `${item.item_type}:${item.id}`;
    setExpandedId((prev) => (prev === key ? null : key));
    if (!item.is_read) {
      if (item.item_type === 'announcement') {
        markAnnouncementRead.mutate(item.id);
      } else {
        markPersonalRead.mutate(item.id);
      }
    }
  };

  const headerSubtitle = useMemo(() => {
    const unread = data?.unread_count ?? 0;
    return unread > 0 ? `${unread} unread` : 'All caught up';
  }, [data?.unread_count]);

  return (
    <div className="space-y-5" data-testid="inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white flex items-center gap-2">
            <InboxIcon className="h-6 w-6 text-primary-600" />
            Inbox
          </h2>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{headerSubtitle}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="search"
            className="input-field pl-9 text-sm"
            placeholder="Search inbox..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            data-testid="inbox-search"
          />
        </div>
      </div>

      <ViewTabs
        tabs={TABS}
        activeId={filter}
        onChange={(id) => {
          setFilter(id);
          setPage(1);
        }}
      />

      <Panel className="overflow-hidden p-0">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-secondary-500">Loading inbox...</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={InboxIcon}
            title="No notifications"
            description="Announcements and personal messages will show up here."
          />
        ) : (
          <ul className="divide-y divide-secondary-100 dark:divide-secondary-700">
            {items.map((item) => {
              const key = `${item.item_type}:${item.id}`;
              const isOpen = expandedId === key;
              const pill = PRIORITY_PILL[item.priority] || PRIORITY_PILL.medium;
              return (
                <li key={key}>
                  <button
                    type="button"
                    className={`w-full text-left px-4 py-3 hover:bg-secondary-50 dark:hover:bg-secondary-800/60 transition-colors ${
                      !item.is_read ? 'bg-primary-50/40 dark:bg-primary-900/10' : ''
                    }`}
                    onClick={() => handleOpen(item)}
                    data-testid={`inbox-item-${item.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-2 rounded-lg bg-secondary-100 dark:bg-secondary-700 flex-shrink-0">
                        {item.item_type === 'announcement' ? (
                          <Megaphone className="h-4 w-4 text-primary-600" />
                        ) : (
                          <User className="h-4 w-4 text-primary-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-0.5">
                          {!item.is_read && (
                            <span className="h-2 w-2 rounded-full bg-primary-600 flex-shrink-0" title="Unread" />
                          )}
                          <span className="font-medium text-secondary-900 dark:text-white truncate">
                            {item.title}
                          </span>
                          <StatusPill status={pill.status} label={pill.label} showDot={false} />
                          {item.item_type === 'announcement' && (
                            <StatusPill
                              status={item.is_expired ? 'At Risk' : 'Completed'}
                              label={item.is_expired ? 'Expired' : 'Active'}
                              showDot
                            />
                          )}
                          <span className="text-[11px] text-secondary-500 ml-auto">
                            {item.created_at
                              ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                              : ''}
                          </span>
                        </div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-300">
                          {isOpen ? item.message : item.preview || item.message}
                        </p>
                        {isOpen && (
                          <div className="mt-2 text-xs text-secondary-500 space-y-1">
                            <div>
                              {item.item_type === 'announcement' ? 'Company announcement' : 'Personal notification'}
                              {item.sent_by_name || item.created_by_name
                                ? ` · from ${item.sent_by_name || item.created_by_name}`
                                : ''}
                            </div>
                            {item.expires_at && (
                              <div>Due {format(new Date(item.expires_at), 'MMM d, yyyy')}</div>
                            )}
                            {item.created_at && (
                              <div>{format(new Date(item.created_at), 'PPpp')}</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-secondary-500">
            Page {page} of {totalPages} ({total} items)
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox;
